import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ISelectionDependentCommandBarItemProps } from "cayo.ui";
import { UNIQUE_GRID_OBJECT_PROP } from "@cayo/admin-components/components/grids/constants";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { IColumn, IActionButton, ActionParameters } from "@cayo/shared/api/types/schema";
import useArray from "@cayo/shared/hooks/useArray";
import { commonMessages } from "@cayo/shared/messages/common";
import { objectUtils } from "@cayo/shared/utils/object";
import { mapMemberProperties } from "./member-mapping";
import { objectSelectionGridMessages } from "./messages";
import { IMemberOfData } from "./types";
import { toContextualMenuItemType } from "../../../utils/fluent-utils";

export const useObjectSelectionGrid = (
  initialItems: any[] = [],
  readonly?: boolean,
  allowRemove?: boolean,
  idPropName: string = "id",
  columns?: IColumn[],
  actions?: IActionButton[]
) => {
  const intl = useIntl();
  const { removeItems, array, addItems } = useArray(initialItems, idPropName);
  const [pickerParams, setPickerParams] = useState<ActionParameters | undefined>(undefined);

  // 1. Define Base Commands
  const selectCommand: ISelectionDependentCommandBarItemProps = {
    key: "select",
    text: intl.formatMessage(objectSelectionGridMessages.selectObjects),
    iconProps: { iconName: "MultiSelect" },
    onClick: () => setPickerParams({}),
  };

  const removeCommand: ISelectionDependentCommandBarItemProps = {
    key: "remove",
    text: intl.formatMessage(commonMessages.remove),
    iconProps: { iconName: "Delete" },
    dependsOnSelection: true,
    multiselect: true,
    onClickWithSelection: (items) => {
      const ids = items?.map((i) => i[idPropName]);
      if (ids?.length) {
        removeItems(ids);
      }
    },
  };

  // 2. Build Commands List
  const commands = useMemo(() => {
    if (readonly) {
      return allowRemove ? [removeCommand] : [];
    }

    if (!actions?.length) {
      return [selectCommand, removeCommand];
    }

    return actions.map((action) => {
      if (!action.key?.startsWith("add")) {
        return removeCommand;
      }

      const additionalParameters = action.actionParameters?.additionalParameters;

      return {
        ...selectCommand,
        key: action.key,
        text: action.text,
        split: action.split,
        onClick: () => setPickerParams({ additionalParameters }),
        subMenuProps: {
          items: (action.subMenuProps ?? []).map((sub) => {
            // Handle Add-with sub-actions
            if (sub.key?.startsWith("add_with")) {
              return {
                ...selectCommand,
                key: sub.key,
                text: sub.text,
                iconProps: undefined,
                subMenuProps: undefined,
                onClick: () =>
                  setPickerParams({
                    additionalParameters: sub.actionParameters?.additionalParameters,
                  }),
              };
            }

            // Map standard sub-actions
            const item = {
              ...sub,
              subMenuProps: undefined,
            } as ISelectionDependentCommandBarItemProps;
            if (sub.action === "add" && sub.actionParameters?.additionalParameters) {
              item.onClick = () => addItems([{ ...sub.actionParameters!.additionalParameters }]);
            }

            item.itemType = toContextualMenuItemType(sub.itemType);

            delete item.type;
            return item;
          }),
        },
      };
    });
  }, [readonly, actions, addItems, intl, allowRemove]);

  // 3. Handlers
  const onAddItems = useCallback(
    (newItems: any[]) => {
      const normalized = normalizePropertyNames(newItems, columns).map((item) => ({
        ...item,
        ...(pickerParams?.additionalParameters || {}),
      }));
      addItems(normalized);
    },
    [addItems, columns, pickerParams]
  );

  const getAddedAndRemovedMembers = useCallback((): IMemberOfData => {
    const initialKeys = new Set(initialItems.map((m) => m[UNIQUE_GRID_OBJECT_PROP]));
    const currentKeys = new Set(array.map((m) => m[UNIQUE_GRID_OBJECT_PROP]));

    const addedMembers = array
      .filter((m) => !initialKeys.has(m[UNIQUE_GRID_OBJECT_PROP]))
      .map(mapMemberProperties);

    const removedMembers = initialItems
      .filter((m) => !currentKeys.has(m[UNIQUE_GRID_OBJECT_PROP]))
      .map((m) => objectUtils.pickFromObject(m, ["distinguishedName", "name", "parentOU"]));

    return {
      addedMembers,
      removedMembers,
    };
  }, [initialItems, array]);

  return {
    commands,
    items: array,
    onAddItems,
    isPickerVisible: !!pickerParams,
    onShowPicker: () => setPickerParams({}),
    hidePicker: () => setPickerParams(undefined),
    getAddedAndRemovedMembers,
  };
};
